import { Collapse, List, Button, Tooltip, Switch } from "antd";
import { EditOutlined, DeleteOutlined, DownOutlined, RightOutlined, CheckOutlined, CloseOutlined, EyeOutlined } from "@ant-design/icons";
import { ItemListNameWrapper, ItemNameText, ItemNameWrapper, SelectedItem, StyledBody, StyledColumn, StyledHeader, StyledIconWrapper } from "./styles";
import { ReactComponent as BoxIcon } from "../../assets/images/BoxIcon.svg";
import { ReactComponent as QuestionMark } from "../../assets/images/question_mark.svg";
import { ReactComponent as ConditionalQuestionIcon } from "../../assets/images/flowchart.svg";
import { StyledButton } from "../../components/styles";
import { useState } from "react";
import ConfirmationModal from "../../components/ConfirmationModal";
import { toast } from "react-toastify";
import { ActivationStatus, Category, SubCategory } from "../../types";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { updateCategory } from "../../redux/features/category/categoryAsyncThunk";
import { updateSubCategory } from "../../redux/features/subCategory/subCategoryAsyncThunk";


export const CollapseSection = ({ itemKey, label, activeKey, children, onClick }: any) => {
  return (
    <Collapse
      size="large"
      activeKey={activeKey}
      expandIcon={({ isActive }) => (
        <div className="ant-collapse-arrow" onClick={(e) => onClick && onClick(e)}>
          {isActive ? <DownOutlined /> : <RightOutlined />}
        </div>
      )}
      items={[
        {
          key: itemKey,
          label,
          children: <StyledBody>{children}</StyledBody>,
        },
      ]}
    />
  );
};

const ItemName: React.FC<{ className?: string; icon?: any; name: string }> = ({ className, icon, name }) => {
  return (
    <>
      <ItemNameWrapper className={className}>
        {icon && icon} <ItemNameText>{name}</ItemNameText>
      </ItemNameWrapper>
    </>
  );
};

interface ItemListProps {
  dataSource: any;
  loading: boolean;
  header?: any;
  onEdit: any;
  onDelete: any;
  onItemClick?: any;
  loadMore?: any;
  isSelected?: any;
  isSelectedItemOnClick?: any;
  isStatus?: boolean;
  name?: string;
  isShowDelete?: boolean
  hasAddPermission?: boolean,
  hasEditPermission?: boolean,
  hasDeletePermission?: boolean,
  hasViewPermission?: boolean
}

export const ItemList: React.FC<ItemListProps> = ({ name, header, dataSource, loading, onEdit, onDelete, onItemClick, loadMore, isSelected, isSelectedItemOnClick, isStatus, isShowDelete = true, hasAddPermission, hasEditPermission, hasDeletePermission, hasViewPermission = false }) => {
  const { loading: categoryLoading } = useAppSelector((state) => state.categories);
  const { loading: subCategoryLoading } = useAppSelector((state) => state.subCategories);
  const dispatch = useAppDispatch();
  const [showDeactivationConfirm, setShowDeactivationConfirm] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<Category | SubCategory | null>(null);

  const handleStatusChangeConfirm = async () => {
    try {
      if (selectedItem?.id) {
        if (name === "Category") {
          await dispatch(updateCategory({ id: selectedItem.id, name: selectedItem.name, status: selectedItem?.status === ActivationStatus.Active ? ActivationStatus.Inactive : ActivationStatus.Active })).unwrap();
        } else if (name === "Sub Category") {
          await dispatch(updateSubCategory({ id: selectedItem.id, data: { name: selectedItem.name, status: selectedItem?.status === ActivationStatus.Active ? ActivationStatus.Inactive : ActivationStatus.Active, category: (selectedItem as SubCategory)?.category?.id } })).unwrap();
        }
        setShowDeactivationConfirm(false);
      }
    } catch (error: any) {
      toast.error(error || "Failed to change user status.");
    }
  };

  return (
    <>
      <List
        className="category-list-item"
        loading={loading}
        dataSource={dataSource}
        {...(loadMore && { loadMore: loadMore })}
        {...(header && { header: header })}
        renderItem={(item: any) => (
          <SelectedItem isSelected={isSelected ? item.id === isSelected : false} onClick={() => isSelectedItemOnClick && isSelectedItemOnClick(item)}>
            <List.Item
              actions={[
                isStatus && hasEditPermission && (
                  <Switch
                    checkedChildren={<CheckOutlined />}
                    unCheckedChildren={<CloseOutlined />}
                    checked={item.status === 1}
                    onChange={() => {
                      setSelectedItem(item);
                      setShowDeactivationConfirm(true);
                    }}
                  />
                ),
                item?.hasSubQuestion === true ? (
                  <StyledIconWrapper>
                    <ConditionalQuestionIcon />
                  </StyledIconWrapper>
                ) : null,
                hasEditPermission && <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(item)} type="link" />,
                isShowDelete && hasDeletePermission && <Button size="small" icon={<DeleteOutlined />} onClick={() => onDelete(item)} type="link" danger />,
                !hasEditPermission && !hasAddPermission && hasViewPermission && <Button size="small" icon={<EyeOutlined />} onClick={() => onEdit(item)} type="link" />,
              ].filter(Boolean)}
            >
              <Tooltip placement="topLeft" title={item.name || item.fieldName}>
                <ItemListNameWrapper onClick={() => onItemClick && onItemClick()}>
                  <ItemName icon={item.name ? <BoxIcon /> : <QuestionMark />} name={item.name || item.fieldName} />
                </ItemListNameWrapper>
              </Tooltip>
            </List.Item>
          </SelectedItem>
        )}
      />

      {showDeactivationConfirm && (
        <ConfirmationModal
          handleSave={handleStatusChangeConfirm}
          isModalOpen={showDeactivationConfirm}
          onCancel={() => setShowDeactivationConfirm(false)}
          title={`Confirm ${selectedItem?.status === ActivationStatus.Active ? "Deactivation" : "Activation"} ${name}`}
          message={`Are you sure you want to ${selectedItem?.status === ActivationStatus.Active ? "deactivate" : "activate"} ${name}?`}
          confirmButtonText={selectedItem?.status === ActivationStatus.Active ? "Deactivate" : "Activate"}
          loading={categoryLoading || subCategoryLoading}
        />
      )}
    </>
  );
};

interface ItemLabelProps {
  title: string;
  buttonLabel: string;
  onButtonClick?: any;
  onTitleClick?: any;
  hasAddPermission: boolean,
}

export const ItemLabel: React.FC<ItemLabelProps> = ({ title, buttonLabel, onButtonClick, onTitleClick, hasAddPermission }) => {
  return (
    <StyledHeader onClick={() => onTitleClick && onTitleClick()}>
      <span>{title}</span>
      {(hasAddPermission) && <StyledButton
        type="primary"
        onClick={(e) => {
          onButtonClick && onButtonClick(e);
        }}
      >
        {buttonLabel}
      </StyledButton>}
    </StyledHeader>
  );
}

interface ItemColumnProps {
  title: string;
  buttonLabel: string;
  onAddClick: () => void;
  dataSource: any;
  loading: boolean;
  header?: any;
  onEdit: any;
  onDelete: any;
  onItemClick?: any;
  loadMore?: any;
  isSelected?: any;
  isSelectedItemOnClick?: any;
  isStatus?: boolean;
  isShowDelete?: boolean,
  hasAddPermission: boolean,
  hasEditPermission: boolean,
  hasDeletePermission: boolean
  hasViewPermission: boolean
}

export const ItemColumn: React.FC<ItemColumnProps> = ({ title, buttonLabel, onAddClick, dataSource, loading, isSelected, isSelectedItemOnClick, onEdit, onDelete, header, loadMore, onItemClick, isStatus, isShowDelete = true, hasAddPermission, hasEditPermission, hasDeletePermission, hasViewPermission = false }) => (
  <StyledColumn>
    <ItemLabel title={title} buttonLabel={buttonLabel} onButtonClick={onAddClick} hasAddPermission={hasAddPermission} />
    <StyledBody>
      <ItemList
        name={title}
        isStatus={isStatus}
        header={header}
        loading={loading}
        dataSource={dataSource}
        isSelected={isSelected}
        isSelectedItemOnClick={isSelectedItemOnClick}
        onEdit={onEdit}
        hasEditPermission={hasEditPermission}
        hasDeletePermission={hasDeletePermission}
        hasViewPermission={hasViewPermission}
        onDelete={onDelete}
        loadMore={loadMore}
        onItemClick={onItemClick}
        isShowDelete={isShowDelete}
      />
    </StyledBody>
  </StyledColumn>
);
